// 먹기: 젓가락·면발, 한입 자국, 단계별 팔·입 동작, 손에 든 그릇 위치, 들고 다니며 마시기.
// 프로토타입 1458–1612 (biteMat 제외), pose의 먹기 1966–1999·마시기 2040–2057, 컵 2532–2557, 접시 튀어나오기(tick).
import * as THREE from 'three';
import type { DishId } from '@capy/shared';
import { S } from '../engine/geometry';
import { plush, smooth } from '../engine/materials';
import { easeOutBounce, rnd } from '../engine/math';
import { poseHooks, type PoseVals } from '../capy/pose';
import type { Capy } from '../capy/agent';
import { dish, type Dish, type Step } from './dishes';

/** 한 입에 걸리는 기본 시간 (초). */
export const BITE = 1.7;
/** 한 입 먹을 때 팔: [앞으로 드는 정도, 옆으로 모으는 정도(+는 바깥, -는 안쪽)]. */
const ARM_HAND: [number, number] = [0.3, -1.1];
const ARM_STRAW: [number, number] = [0.25, -0.6];
/** 그릇·병을 기울여 마실 때 (프로토타입 그대로). */
const ARM_TILT: [number, number] = [0.95, 0.35];
/** 들고 다니는 컵 크기 배율. */
export const CARRY_S = 1.4;

export interface Eating {
  start: number;
  stepStart: number;
  si: number;
  steps: Step[];
  done: () => void;
  /** 젓가락·든 그릇을 붙일 방의 루트. */
  world: THREE.Object3D;
}
/** 한 프레임의 먹기 진행 (placeHeld가 쓴다). */
export interface EatFx { st: Step; lead: number; endBack: number; active: boolean; ph: number; up: number; bi: number }
export interface Carry {
  obj: THREE.Object3D; st: Step; key: DishId; sips: number; nb: number; up: number; sipT: number; next: number;
  /** 0 = 손에, 1 = 머리 위에 (헤엄칠 때·올라탔을 때). */
  head: number;
  emptyAt: number;
  pop: number;
  world: THREE.Object3D;
}

const chopMat = plush('#7A2E22', '#B0584A', 0.05);
function makeSticks(st: Step): THREE.Group {
  const g = new THREE.Group(), col = st.morsel || st.morsels![0]!;
  [-0.012, 0.012].forEach((x) => { const s = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.01, 0.56, 6), chopMat); s.rotation.x = Math.PI / 2; s.position.set(x, 0, 0.27); g.add(s); });
  const tip = new THREE.Group(); tip.position.set(0, -0.02, 0.54); g.add(tip); g.userData.morsel = tip;
  const cm = new THREE.MeshStandardMaterial({ color: col, roughness: 0.8 }); g.userData.colorMat = cm;
  if (st.morselShape === 'sushi') {
    const r = new THREE.Mesh(S(0.045, 10, 8), smooth('#FBFAF5')); r.scale.set(0.85, 0.6, 1.3); tip.add(r);
    const tp = new THREE.Mesh(new THREE.BoxGeometry(0.075, 0.02, 0.13), cm); tp.position.y = 0.03; tip.add(tp);
  } else if (st.morselShape === 'kimbap') {
    const kb = new THREE.Group(); kb.rotation.x = 0.9; tip.add(kb);
    kb.add(new THREE.Mesh(new THREE.CylinderGeometry(0.048, 0.048, 0.05, 16), smooth('#1F2A22')));
    const rice = new THREE.Mesh(new THREE.CylinderGeometry(0.042, 0.042, 0.052, 16), smooth('#FBFAF5')); kb.add(rice);
    ([['#F2D35A', 0.012, 0], ['#F07A3A', -0.012, 0.01], ['#5E9B4F', 0, -0.014], ['#C8323C', 0, 0.016]] as const).forEach(([c, dx, dz]) => { const f = new THREE.Mesh(new THREE.BoxGeometry(0.014, 0.054, 0.014), smooth(c)); f.position.set(dx, 0, dz); kb.add(f); });
  } else if (st.morselShape === 'slice') {
    const sl = new THREE.Mesh(new THREE.BoxGeometry(0.075, 0.035, 0.17), cm); sl.rotation.y = Math.PI / 2; tip.add(sl);
  } else if (!st.noodle) {
    const m = new THREE.Mesh(S(0.045, 10, 8), cm); m.scale.set(1.2, 0.8, 1); tip.add(m);
  }
  g.scale.setScalar(1.5);
  return g;
}

const noodleMat = plush('#C9B08A', '#EEDDBE', 0.05);
const noodleGeo = (() => { const g = new THREE.CylinderGeometry(0.008, 0.008, 1, 5); g.translate(0, -0.5, 0); return g; })();
let curly: THREE.TubeGeometry | null = null;
const curlyMats: Record<string, THREE.MeshStandardMaterial> = {};
function curlyGeo(): THREE.TubeGeometry {
  if (curly) return curly;
  const pts: THREE.Vector3[] = [];
  for (let k = 0; k <= 60; k++) { const u = k / 60; pts.push(new THREE.Vector3(0.012 * Math.sin(u * 44), -u, 0.012 * Math.cos(u * 37))); }
  return (curly = new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 90, 0.0068, 5));
}
function makeNoodles(st: Step): THREE.Group {
  const g = new THREE.Group();
  if (st.curly) {
    const m = (curlyMats[st.morsel!] ||= plush(st.morsel!, '#FFF2C8', 0.06));
    for (let i = 0; i < 7; i++) { const n = new THREE.Mesh(curlyGeo(), m); n.position.set((i - 3) * 0.014, 0, (i % 2 ? 0.01 : -0.01)); n.rotation.y = i * 0.9; n.userData.k = 0.75 + 0.25 * rnd(i + 90); g.add(n); }
    return g;
  }
  for (let i = 0; i < 7; i++) { const n = new THREE.Mesh(noodleGeo, noodleMat); n.position.set((i - 3) * 0.014, 0, (i % 2 ? 0.01 : -0.01)); n.userData.k = 0.75 + 0.25 * rnd(i + 90); g.add(n); }
  return g;
}

const bInv = new THREE.Matrix4(), bV = new THREE.Vector3();
function applyBites(st: Step, n: number) {
  st.edible.forEach((m) => {
    const ub = ((m as THREE.Mesh).material as THREE.Material).userData as { bites?: THREE.Vector4[]; n: { value: number } };
    if (!ub || !ub.bites) return;
    m.updateMatrix(); bInv.copy(m.matrix).invert();
    const sc = (m.scale.x + m.scale.y + m.scale.z) / 3 || 1;
    for (let i = 0; i < 4; i++) {
      const b = st.biteAt![i];
      if (!b || i >= n) { ub.bites[i]!.set(0, 0, 0, 0); continue; }
      bV.set(b[0], b[1], b[2]).applyMatrix4(bInv); ub.bites[i]!.set(bV.x, bV.y, bV.z, b[3] / sc);
    }
    ub.n.value = Math.min(4, n);
  });
}

/** nb번 중 n번 먹은 모습으로: 한입 자국, 하나씩 사라지기, 국물 수위 내리기, 줄어들기. */
export function setEaten(st: Step, n: number, nb: number): void {
  if (st.residue) { const lvl = st.residue.topY * (1 - n / nb); st.residue.items.forEach((m) => { m.visible = lvl < (m.userData.y as number); }); }
  if (st.biteAt) {
    if (n >= nb) { st.edible.forEach((m) => { m.visible = false; }); return; }
    st.edible.forEach((m) => { m.visible = true; }); applyBites(st, n); return;
  }
  st.edible.forEach((m, i) => {
    if (st.perItem) { m.visible = i >= n; return; }
    // 위에 얹은 고명(불닭의 깨·김·파)은 첫 입에 없어진다
    if (m.userData.firstBite) { m.visible = n === 0; return; }
    const k = 1 - n / nb, b = m.userData.base as THREE.Vector3;
    if (m.userData.level) { const L = m.userData.level, y = L.y0 + (L.y1 - L.y0) * k, r = L.rAt(y); m.position.y = y; m.scale.set(r, 1, r); m.visible = k > 0.02; return; }
    if (m.userData.yOnly) m.scale.set(b.x, Math.max(0.001, b.y * k), b.z); else m.scale.copy(b).multiplyScalar(Math.max(0.001, k));
    m.visible = k > 0.02;
  });
}

const vP = new THREE.Vector3(), vQ = new THREE.Vector3(), vM = new THREE.Vector3(), vB = new THREE.Vector3(), vG = new THREE.Vector3(), qA = new THREE.Quaternion();
/** 젓가락 기본 끝 거리 (0.54 × 1.5)와 늘이고 줄이는 범위. */
const STICK_REACH = 0.54 * 1.5, STICK_STRETCH = [0.85, 1.45] as const;
/** 빨대 컵은 입까지 다 가져가지 않고 앞발 쪽에 남겨 옆으로 든 모양으로 (1이면 입 바로 아래). */
const STRAW_TO_MOUTH = 0.75;

/** 젓가락 끝, 손에 든 그릇·병 위치를 맞춘다. */
function placeHeld(a: Capy, fx: EatFx) {
  const st = fx.st, c = a.c, world = a.eating?.world ?? c.root.parent!;
  c.root.updateMatrixWorld(true);
  c.paws[0]!.getWorldPosition(vP);
  if (st.two) { c.paws[1]!.getWorldPosition(vQ); vP.add(vQ).multiplyScalar(0.5); }
  c.headG.localToWorld(vM.set(0, -0.28, 1.12));
  if (st.mode === 'chop') {
    if (!a.sticks) { a.sticks = makeSticks(st); world.add(a.sticks); }
    const sticks = a.sticks;
    if (st.morsels) (sticks.userData.colorMat as THREE.MeshStandardMaterial).color.set(st.morsels[Math.min(st.morsels.length - 1, fx.bi)]!);
    const tgt = st.targets ? st.targets[Math.min(st.targets.length - 1, fx.bi)]! : st.container!;
    tgt.getWorldPosition(vB); vB.y += st.noodle ? 0.22 : 0.1;
    vQ.copy(vB).lerp(vM, fx.up);
    // 손잡이는 항상 앞발에, 끝은 음식(또는 입)으로. 거리에 맞춰 젓가락 길이를 조금 늘이거나 줄인다 (프로토타입은 손잡이가 손에서 떨어졌다).
    const dl = vP.distanceTo(vQ) || 1;
    sticks.position.copy(vP);
    sticks.scale.set(1.5, 1.5, 1.5 * Math.min(STICK_STRETCH[1], Math.max(STICK_STRETCH[0], dl / STICK_REACH)));
    sticks.lookAt(vQ);
    const morsel = sticks.userData.morsel as THREE.Group;
    morsel.visible = fx.active && fx.ph > 0.36 && fx.ph < 0.8;
    if (st.noodle) {
      if (!a.noodles) { a.noodles = makeNoodles(st); world.add(a.noodles); }
      const show = fx.active && fx.ph > 0.33 && fx.ph < 0.86;
      a.noodles.visible = show;
      if (show) {
        sticks.updateMatrixWorld(true); morsel.getWorldPosition(vQ);
        a.noodles.position.copy(vQ);
        const toBowl = Math.max(0.02, vQ.y - (vB.y - 0.12)), Lmax = Math.min(0.45, toBowl);
        const L = fx.ph < 0.75 ? Lmax : Lmax * (1 - (fx.ph - 0.75) / 0.11);
        a.noodles.children.forEach((n, i) => { n.scale.set(1, Math.max(0.001, L * n.userData.k), 1); n.rotation.z = 0.06 * Math.sin(fx.ph * 20 + i); });
        a.noodles.rotation.y = a.facing;
      }
    }
    return;
  }
  c.headG.localToWorld(st.straw ? vM.set(0, -0.98, 1.32) : vM.set(0, st.mode === 'drink' ? -0.45 : -0.36, st.mode === 'drink' ? (st.two ? 1.78 : 1.55) : 1.42));
  if (st.lids && !st.opened && fx.lead > 0.55) { st.opened = true; st.lids.forEach((l) => { l.visible = false; }); if (st.lidRest) st.lidRest.visible = true; }
  const h = st.held!;
  if (!st.picked) { st.home = { pos: h.position.clone(), rot: h.rotation.clone(), parent: h.parent! }; st.from = h.getWorldPosition(new THREE.Vector3()); world.attach(h); st.picked = true; }
  const lead = fx.lead * (1 - fx.endBack);
  h.rotation.set(st.mode === 'drink' && !st.straw ? -1.15 * fx.up * lead : 0, a.facing + (st.yaw || 0), (st.tiltZ || 0) * lead, 'YXZ');
  if (st.gripAt) {
    // 쥐는 지점이 앞발에서 출발해, 지금 먹을 조각이 입에 닿는 위치까지 올라간다.
    // 한 입 먹을 때는 꼬치가 앞발→입 방향을 향해, 쥔 끝이 앞발 가까이에 남는다.
    // 하나씩 먹는 꼬치(당고)는 남은 조각이 한 칸씩 위로 미끄러져 올라와, 늘 맨 위 자리를 입에 댄다 (팔이 짧아 아래 조각은 입에 닿지 않는다)
    if (st.perItem) {
      st.edible.forEach((m) => { m.userData.slot0 ??= m.position.clone(); });
      // 새 한 입의 처음 1/4 동안 한 칸 위로 (프레임 속도와 무관하게 한 입 진행 비율로)
      const k = fx.bi > 0 && fx.active ? Math.min(1, fx.ph * 4) : 1;
      for (let j = fx.bi; j < st.edible.length; j++) st.edible[j]!.position.lerpVectors(st.edible[Math.min(st.edible.length - 1, j - fx.bi + (fx.bi > 0 ? 1 : 0))]!.userData.slot0, st.edible[j - fx.bi]!.userData.slot0, k);
    }
    const piece = st.edible[Math.min(st.edible.length - 1, fx.bi)]!;
    vB.copy(st.perItem ? st.edible[0]!.userData.slot0 : piece.position).sub(vG.set(...st.gripAt));
    qA.setFromUnitVectors(vB.clone().normalize(), vQ.copy(vM).sub(vP).normalize());
    h.quaternion.slerp(qA, fx.up);
    vB.applyQuaternion(h.quaternion).multiply(h.scale);
    vQ.copy(vM).sub(vB);
    vQ.lerpVectors(vP, vQ, fx.up);
    if (lead < 1) vQ.lerpVectors(st.from!, vQ, lead);
    h.position.copy(vQ).sub(vG.set(...st.gripAt).applyQuaternion(h.quaternion).multiply(h.scale));
    return;
  }
  vQ.copy(vP).lerp(vM, 0.35 + (st.straw ? STRAW_TO_MOUTH - 0.35 : 0.65) * fx.up); vQ.y -= st.grip! * 1.5 * (1 - 0.6 * fx.up);
  if (lead < 1) vQ.lerpVectors(st.from!, vQ, lead);
  h.position.copy(vQ);
  if (st.strawMesh) aimStraw(st.strawMesh, c, fx.up * lead);
}

const sY = new THREE.Vector3(0, 1, 0), sBase = new THREE.Vector3(), sTip = new THREE.Vector3(), sDir = new THREE.Vector3(), sQ = new THREE.Quaternion(), sInv = new THREE.Matrix4();

/**
 * 마시는 동안(k: 0 평소 → 1 한 모금) 빨대 끝을 입으로 돌린다. 빨대 아래 끝(컵 안)은 그대로 두고,
 * 각도와 길이(0.8~1.5배)를 바꿔 끝이 입에 닿게 한다. 프로토타입은 빨대가 고정 각도라 입과 어긋났다.
 */
function aimStraw(straw: THREE.Mesh, c: Capy['c'], k: number) {
  const u = straw.userData as { rest?: { pos: THREE.Vector3; quat: THREE.Quaternion; base: THREE.Vector3; len: number } };
  if (!u.rest) {
    const len = (straw.geometry as THREE.CylinderGeometry).parameters.height;
    u.rest = { pos: straw.position.clone(), quat: straw.quaternion.clone(), base: new THREE.Vector3(0, -len / 2, 0).applyQuaternion(straw.quaternion).add(straw.position), len };
  }
  const R = u.rest;
  if (k <= 0.001) { straw.position.copy(R.pos); straw.quaternion.copy(R.quat); straw.scale.set(1, 1, 1); return; }
  // 입 위치를 컵(빨대 부모) 좌표로
  c.root.updateMatrixWorld(true); straw.parent!.updateMatrixWorld(true);
  c.headG.localToWorld(sTip.set(0, -0.28, 1.1));
  sTip.applyMatrix4(sInv.copy(straw.parent!.matrixWorld).invert());
  sDir.copy(sTip).sub(R.base);
  const want = Math.min(R.len * 1.5, Math.max(R.len * 0.8, sDir.length()));
  sQ.setFromUnitVectors(sY, sDir.normalize());
  straw.quaternion.copy(R.quat).slerp(sQ, k);
  const len = R.len + (want - R.len) * k;
  straw.scale.set(1, len / R.len, 1);
  straw.position.copy(R.base).add(sBase.set(0, len / 2, 0).applyQuaternion(straw.quaternion));
}

/** 들고 다니는 컵: 손에, 또는 헤엄칠 때 머리 위에. */
function placeCarry(a: Capy, t: number) {
  const C = a.carry!, c = a.c, o = C.obj, k = a.size || 1;
  c.root.updateMatrixWorld(true);
  c.paws[0]!.getWorldPosition(vP);
  const base = o.userData.base as number, sc = base * CARRY_S * k;
  c.headG.localToWorld(vM.set(0, -0.3, 1.18)); vM.y -= (C.st.straw ? 0.55 : 0.16) * sc; vM.x += Math.sin(a.facing) * 0.08 * sc; vM.z += Math.cos(a.facing) * 0.08 * sc;
  vQ.copy(vP); vQ.y -= C.st.grip! * 1.05 * sc; vQ.lerp(vM, C.up * (C.st.straw ? STRAW_TO_MOUTH : 1));
  c.itemAnchor.localToWorld(vB.set(0, a.item ? 0.3 : 0.02, 0));
  o.position.lerpVectors(vQ, vB, C.head);
  o.rotation.set(C.st.straw ? 0 : -1.15 * C.up, a.facing, 0, 'YXZ');
  const pop = C.pop ? Math.min(1, (t - C.pop) / 0.5) : 1;
  if (pop >= 1) C.pop = 0;
  o.scale.setScalar(base * CARRY_S * k * (pop < 1 ? 1 - Math.pow(1 - pop, 3) : 1));
  if (C.st.strawMesh) aimStraw(C.st.strawMesh, c, C.up * (1 - C.head));
  if (!a.isPlayer && C.sips >= C.nb) { if (!C.emptyAt) C.emptyAt = t + 4; else if (t > C.emptyAt) dropCarry(a, false, t); }
}

function endStep(a: Capy, st: Step) {
  if (st.mode === 'chop') {
    if (a.sticks) { a.sticks.parent?.remove(a.sticks); a.sticks = null; }
    if (a.noodles) { a.noodles.parent?.remove(a.noodles); a.noodles = null; }
    return;
  }
  const h = st.held!;
  if (!st.picked) return;
  if (st.ret) { st.home!.parent.add(h); h.position.copy(st.home!.pos); h.rotation.copy(st.home!.rot); h.scale.set(1, 1, 1); }
  else if (h.parent) h.parent.remove(h);
}

export function cleanupEating(a: Capy): void {
  if (a.sticks) { a.sticks.parent?.remove(a.sticks); a.sticks = null; }
  if (a.noodles) { a.noodles.parent?.remove(a.noodles); a.noodles = null; }
  const e = a.eating;
  if (e) e.steps.forEach((st) => { if (st.held && st.picked && st.held.parent === e.world) e.world.remove(st.held); });
  a.eating = null; a.fx = null;
}

/** 접시가 튀어나오듯 커지는 애니메이션 목록 (tickPops로 진행). */
const pops: { g: THREE.Object3D; t0: number }[] = [];

/** spot(상 위 자리)에 음식을 차리고 1초 뒤부터 먹기 시작한다. */
export function serve(a: Capy, key: DishId, spot: THREE.Vector3, world: THREE.Object3D, t: number, done: () => void = () => {}): Dish {
  cleanupEating(a);
  if (a.dish && a.dish.group.parent) a.dish.group.parent.remove(a.dish.group);
  const d = dish(key);
  d.group.position.copy(spot); d.group.rotation.y = a.facing + (rnd(spot.x * 3 + key.length) - 0.5) * 0.3; d.group.scale.setScalar(0.01); world.add(d.group);
  pops.push({ g: d.group, t0: t });
  a.dish = d;
  a.eating = { start: t + 1.0, stepStart: t + 1.0, si: 0, steps: d.steps, done, world };
  return d;
}

export function tickPops(t: number): void {
  for (let i = pops.length - 1; i >= 0; i--) {
    const q = Math.min(1, (t - pops[i]!.t0) / 0.55);
    pops[i]!.g.scale.setScalar(Math.max(0.01, 1.5 * easeOutBounce(q)));
    if (q >= 1) pops.splice(i, 1);
  }
}

// ---------- 들고 다니는 컵 (라군 슬러시 바) ----------
const tosses: { obj: THREE.Object3D; from: THREE.Vector3; to: THREE.Vector3; t0: number; s0: number }[] = [];

export function giveCarry(a: Capy, key: DishId, world: THREE.Object3D, t: number): void {
  dropCarry(a, true, t);
  const d = dish(key);
  world.add(d.group); d.group.updateMatrixWorld(true);
  const st = d.steps[0]!, obj = st.held!;
  world.attach(obj); world.remove(d.group);
  obj.userData.base = obj.scale.x;
  a.carry = { obj, st, key, sips: 0, nb: st.bites, up: 0, sipT: -1, next: t + 2.5 + Math.random() * 3, head: 0, emptyAt: 0, pop: t, world };
  setEaten(st, 0, st.bites); obj.scale.setScalar(0.01);
}

/** 컵 내려놓기. instant가 아니면 앞으로 휙 던져 사라진다. */
export function dropCarry(a: Capy, instant: boolean, t: number): void {
  const C = a.carry;
  if (!C) return;
  a.carry = null;
  if (instant) { if (C.obj.parent) C.obj.parent.remove(C.obj); }
  else {
    const from = C.obj.position.clone(), to = from.clone();
    to.x += Math.sin(a.facing) * 1.6; to.z += Math.cos(a.facing) * 1.6; to.y = 0.1;
    tosses.push({ obj: C.obj, from, to, t0: t, s0: C.obj.scale.x });
  }
}

export function updateTosses(t: number): void {
  for (let i = tosses.length - 1; i >= 0; i--) {
    const q = tosses[i]!, p = Math.min(1, (t - q.t0) / 0.7);
    q.obj.position.lerpVectors(q.from, q.to, p); q.obj.position.y += Math.sin(p * Math.PI) * 0.9; q.obj.rotation.x = p * 5;
    q.obj.scale.setScalar(Math.max(0.001, q.s0 * (1 - p * p)));
    if (p >= 1) { if (q.obj.parent) q.obj.parent.remove(q.obj); tosses.splice(i, 1); }
  }
}

// ---------- 자세 훅 ----------
poseHooks.eating = (a, t, v: PoseVals) => {
  const e = a.eating;
  if (!e || t < e.start) return;
  let st = e.steps[e.si], tt = t - e.stepStart;
  if (st && tt >= 0.5 + st.bites * (st.biteDur || BITE) + 0.5) { endStep(a, st); e.si++; e.stepStart = t; tt = 0; st = e.steps[e.si]; if (!st) { a.eating = null; e.done(); } }
  if (!st) return;
  const BD = st.biteDur || BITE, nb = st.bites, bt = tt - 0.5, lead = Math.min(1, tt / 0.5), endBack = Math.max(0, Math.min(1, (bt - nb * BD) / 0.5));
  const active = bt >= 0 && bt < nb * BD, bi = Math.min(nb - 1, Math.max(0, Math.floor(bt / BD))), ph = active ? (bt - bi * BD) / BD : 0;
  let up = 0;
  if (st.mode === 'chop') {
    if (active) up = ph < 0.35 ? 0 : ph < 0.75 ? Math.sin((ph - 0.35) / 0.4 * Math.PI / 2) : 1 - (ph - 0.75) / 0.25;
    v.armR = -0.35 * lead * (1 - endBack) - 0.95 * up; v.armRz = 0.5 * up; v.hx += 0.14 * (1 - up) * lead * (1 - endBack);
    if (active && ph > 0.58 && ph < 0.82) v.mouthOpen = Math.max(v.mouthOpen, 0.95 * Math.sin(Math.min(1, (ph - 0.58) / 0.2) * Math.PI / 2));
    if (active && ph >= 0.82 && ph < 0.86) v.mouthOpen = Math.max(v.mouthOpen, 0.95 * (1 - (ph - 0.82) / 0.04));
    if (active && ph >= 0.84 && ph < 0.9) a.chewUntil = t + 1.1;
  } else {
    if (active) up = ph < 0.45 ? Math.sin(ph / 0.45 * Math.PI / 2) : ph < 0.65 ? 1 : 1 - (ph - 0.65) / 0.35;
    // 팔 동작 (프로토타입과 다르게 바꿈, docs/decisions.md):
    // 손으로 먹기는 앞발을 사선 안쪽 턱 아래로, 빨대 컵은 입 옆 아래로 모은다. 그릇·병을 기울여 마실 때만 앞으로 높이 든다.
    const hold = lead * (1 - endBack);
    const [rise, side] = st.mode === 'hand' ? ARM_HAND : st.straw ? ARM_STRAW : ARM_TILT;
    const raise = -0.3 * hold - rise * up;
    v.armR = raise; v.armRz = side * up;
    if (st.two) { v.armL = raise; v.armLz = -side * up; }
    if (st.mode === 'drink' && !st.straw) v.hx -= 0.32 * up; else v.hx += 0.06 * up;
    if (st.mode === 'drink') {
      if (active && ph > 0.35 && ph < 0.72) { v.mouthOpen = Math.max(v.mouthOpen, 0.35); v.hx += 0.035 * Math.sin(t * 11); }
    } else {
      if (active && ph > 0.3 && ph < 0.5) v.mouthOpen = Math.max(v.mouthOpen, 0.95 * Math.sin(Math.min(1, (ph - 0.3) / 0.17) * Math.PI / 2));
      if (active && ph >= 0.5 && ph < 0.55) v.mouthOpen = Math.max(v.mouthOpen, 0.95 * (1 - (ph - 0.5) / 0.05));
      if (active && ph >= 0.53 && ph < 0.6) a.chewUntil = t + 1.1;
    }
  }
  const eaten = st.mode === 'drink' ? (active ? bi + Math.min(1, Math.max(0, (ph - 0.35) / 0.32)) : (bt >= nb * BD ? nb : 0))
    : active ? bi + (ph > (st.mode === 'chop' ? 0.4 : 0.55) ? 1 : 0) : (bt >= nb * BD ? nb : 0);
  setEaten(st, eaten, nb);
  a.fx = { st, lead, endBack, active, ph, up, bi };
};

/** 컵 들고 가끔 한 모금. 라군에서 헤엄치거나 올라타면 머리 위에 얹는다. */
poseHooks.carry = (a, t, dt, v, moving, ctx) => {
  const C = a.carry;
  if (!C) return;
  const wantHead = (ctx.room === 'lagoon' && a.bathAmt > 0.5) || a.riding || a.hop ? 1 : 0;
  C.head += (wantHead - C.head) * Math.min(1, dt * 3);
  if (C.sipT < 0 && C.head < 0.05 && !moving && C.sips < C.nb && t > C.next) C.sipT = t;
  let up = 0;
  if (C.sipT >= 0) {
    const p = (t - C.sipT) / 2.4;
    if (p >= 1 || C.head > 0.3) { C.sipT = -1; if (p >= 1) { C.sips++; setEaten(C.st, C.sips, C.nb); } C.next = t + 5 + Math.random() * 6; }
    else { up = p < 0.3 ? Math.sin(p / 0.3 * Math.PI / 2) : p < 0.7 ? 1 : 1 - (p - 0.7) / 0.3; if (p > 0.32 && p < 0.68) { v.mouthOpen = Math.max(v.mouthOpen, 0.35); v.hx += 0.035 * Math.sin(t * 11); } }
  }
  C.up = up;
  const hold = 1 - C.head;
  const [rise, side] = C.st.straw ? ARM_STRAW : ARM_TILT;
  v.armR = v.armR * (1 - hold) + (-0.6 - (rise - 0.15) * up) * hold; v.armRz = v.armRz * (1 - hold) + side * up * hold;
  v.hx += (C.st.straw ? 0.06 : -0.32) * up * hold;
  if (C.head > 0.5) v.hx -= 0.08 * C.head;
};

poseHooks.held = (a, t) => {
  if (a.fx) placeHeld(a, a.fx);
  if (a.carry) placeCarry(a, t);
};
